import React, { useContext } from 'react';
import styled from 'styled-components';
import type { BannerLayout } from '../../@types/Content';
import { ContextServiceTracking } from '../FeatureApp';
import { trackBannerClickEvent } from '../tracking/trackingService';

interface MaybeLinkProps {
  children: React.ReactNode;
  ctaUrl?: string;
  alignment: string;
  fullWidth: boolean;
  ariaLabel: string;
  bannerLayout: BannerLayout;
}

const getTrackingLayoutValue = (layout: BannerLayout) => {
  switch (layout) {
    case 'imageOnly':
      return 'image';
    case 'textOnly':
      return 'text';
    case 'imageAndText':
      return 'image + text';
    default:
      return layout;
  }
};

const getTrackingPosition = (fullWidth: boolean, alignment: string) => {
  return fullWidth ? 'full width' : alignment;
};

const getInternalTrackingUrl = (ctaUrl: string) => {
  if (typeof window === 'undefined') return ctaUrl;

  return new URL(ctaUrl, window.location.origin).href;
};

export const MaybeLink: React.FC<MaybeLinkProps> = ({
  children,
  ctaUrl,
  fullWidth,
  ariaLabel,
  bannerLayout,
  alignment,
}: MaybeLinkProps) => {
  const serviceTracking = useContext(ContextServiceTracking);

  const onBannerClick = () => {
    const linkType = serviceTracking?.evaluateLinkType(ctaUrl ?? '') ?? '';
    const dataLayerEvent = {
      eventInfo: {
        eventAction: linkType,
        eventName: 'pencil banner - click on pencil banner',
      },
      attributes: {
        componentName: 'pencil-banner',
        label: '',
        targetURL: linkType === 'internal_link' ? getInternalTrackingUrl(ctaUrl ?? '') : '',
        elementName: 'image',
        value: getTrackingLayoutValue(bannerLayout),
        pos: getTrackingPosition(fullWidth, alignment),
      },
    };

    if (serviceTracking) {
      trackBannerClickEvent(serviceTracking, dataLayerEvent);
    }
  };

  return ctaUrl && bannerLayout === 'imageOnly' ? (
    <CustomLink
      data-testid="banner-link"
      href={ctaUrl}
      target="_self"
      fullWidth={fullWidth}
      aria-label={ariaLabel}
      onClick={onBannerClick}
    >
      {children}
    </CustomLink>
  ) : (
    children
  );
};

const CustomLink = styled.a<{ fullWidth: boolean }>`
  ${(props) => props.fullWidth && 'width: 100%;'}
  text-decoration: none;
  height: 100%;
  min-height: 100px;
`;
